import React, { useState, useEffect } from 'react';

import { Dialog, DialogContent, DialogActions, Typography, Box, Stepper, Step, StepLabel, CircularProgress, Button } from '@material-ui/core';
import { Alert } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';

import EvaluationStepContent from './EvaluationStepContent';
import QualityEvaluationService from '../../services/quality-evaluation.service';
import QualityObservationService from '../../services/quality-observation.service';

const useStyles = makeStyles((theme) => ({
  button: {
    height: 40,
    width: 180,
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  dialogContent: {
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Roboto !important',
    padding: '50px 40px 30px !important',
    width: '700px',
  },
  stepper: {
    '& .MuiStepConnector-line': {
      borderColor: '#E6E6E6 !important',
    },
    '& .MuiStepIcon-root': {
      border: '1px solid #2AAAE2 !important',
      borderRadius: '50% !important',
      fill: 'white !important',
    },
    '& .MuiStepIcon-root text': {
      fill: '#2AAAE2FF !important',
    },
    '& .MuiStepIcon-root.MuiStepIcon-active': {
      border: 'none !important',
      fill: '#2AAAE2 !important',
    },
    '& .MuiStepIcon-root.MuiStepIcon-active text': {
      fill: 'white !important',
    },
    '& .MuiStepIcon-root.MuiStepIcon-completed': {
      border: 'none !important',
      fill: '#2AAAE2 !important',
    },

    backgroundColor: 'transparent',
    marginBottom: theme.spacing(2),
    padding: 0,
  },
  'svg.MuiSvgIcon-root': {
    fill: '#B4EAB7 !important',
  },
}));

const TechnologistEvaluationModal = ({ open, onClose, observationId, disableSaving = false, previewMode = false, transcriptionStatus = null }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [answerChecked, setAnswerChecked] = useState(false);

  useEffect(() => {
    if (open && observationId) {
      setActiveStep(0);
      setAnswers({});
      setAnswerChecked(false);
      setQuestions([]);
      setEvaluation(null);

      loadEvaluation();
    }
  }, [open, observationId]);

  useEffect(() => {
    // If modal is open and there are no questions (failed or not generated), close this modal and trigger callback modal
    if (open && !loading && !questions.length && evaluation !== null) {
      if (transcriptionStatus === 'processing') {
        // Still processing, don't close
        return;
      }
      // Close this modal and let parent handle showing callback modal
      onClose(false, true); // Pass true to indicate should show callback modal
    }
  }, [open, loading, questions.length, evaluation, transcriptionStatus]);

  const shuffleOptions = (question) => {
    const shuffledOptions = [...question.options];

    // Fisher-Yates shuffle algorithm
    for (let i = shuffledOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledOptions[i], shuffledOptions[j]] = [shuffledOptions[j], shuffledOptions[i]];
    }

    return {
      ...question,
      options: shuffledOptions,
    };
  };

  const loadEvaluation = async () => {
    if (!observationId) return;

    try {
      setLoading(true);
      const result = await QualityEvaluationService.getByObservationId(observationId);

      setEvaluation(result);

      const shuffledQuestions = (result.questions || []).map((question) => shuffleOptions(question));

      setQuestions(shuffledQuestions);

      const evaluationAnswers = result.answers || {};
      setAnswers(evaluationAnswers);

      if (Object.keys(evaluationAnswers).length > 0) {
        const answeredQuestionIds = Object.keys(evaluationAnswers);
        const nextUnansweredIndex = shuffledQuestions.findIndex((question) => !answeredQuestionIds.includes(question.id.toString()));

        if (nextUnansweredIndex !== -1) {
          setActiveStep(nextUnansweredIndex);
        } else {
          setActiveStep(shuffledQuestions.length);
        }
      }
    } catch (error) {
      console.error('Error loading evaluation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId) => (event) => {
    setAnswers({
      ...answers,
      [questionId]: event.target.value,
    });
  };

  const saveCurrentProgress = async () => {
    if (!evaluation || disableSaving || previewMode) return true;

    try {
      setLoading(true);

      await QualityEvaluationService.submitAnswers(evaluation.id, answers);

      return true;
    } catch (error) {
      console.error('Error saving answers:', error);
      enqueueSnackbar('Error saving your answers', { variant: 'error' });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = () => {
    setAnswerChecked(true);
  };

  const handleNext = async () => {
    const saved = await saveCurrentProgress();
    if (!saved) return;

    setActiveStep((prevStep) => prevStep + 1);
    setAnswerChecked(false);

    const currentQuestion = questions[activeStep];

    if (currentQuestion) {
      setAnswers((prevAnswers) => {
        const newAnswers = { ...prevAnswers };
        delete newAnswers[currentQuestion.id];
        return newAnswers;
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = async () => {
    try {
      setLoading(true);

      if (!disableSaving) {
        if (evaluation) {
          await QualityEvaluationService.submitAnswers(evaluation.id, answers);
        }
        await QualityObservationService.complete(observationId);
      }

      onClose(true);
    } catch (error) {
      console.error('Error saving evaluation:', error);
      enqueueSnackbar('Error saving evaluation', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderBackButtons = () => {
    return activeStep > 0 ? (
      <Button className={classes.button} onClick={handleBack} disabled={loading} variant="outlined">
        Back
      </Button>
    ) : (
      <div />
    );
  };

  const renderButtons = () => {
    if (activeStep === questions.length && questions.length !== 0) {
      return (
        <Button className={classes.button} onClick={handleComplete} color="primary" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : `${previewMode ? 'Continue' : 'Resolved'}`}
        </Button>
      );
    }

    if (questions.length === 0) {
      // If processing, show close button
      return transcriptionStatus === 'processing' ? (
        <Button className={classes.button} onClick={() => onClose(false)} color="primary" variant="contained" disabled={loading}>
          Close
        </Button>
      ) : null;
    }

    return answerChecked ? (
      <Button className={classes.button} onClick={handleNext} color="primary" variant="contained" disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Next'}
      </Button>
    ) : (
      <Button className={classes.button} onClick={handleCheck} color="primary" variant="contained" disabled={loading || (!answers[questions[activeStep]?.id] && !previewMode)}>
        {loading ? <CircularProgress size={24} /> : 'Check'}
      </Button>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      );
    }

    if (!questions.length) {
      // Check if questions are still being generated
      if (transcriptionStatus === 'processing') {
        return (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="200px" gap={2}>
            <CircularProgress size={40} />
            <Typography variant="body1" align="center">
              Questions are being generated...
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Please wait a moment and try again.
            </Typography>
          </Box>
        );
      }

      // If not processing and no questions, the useEffect will auto-close and show callback modal
      return null;
    }

    return (
      <>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {questions.map((question) => (
            <Step key={question.id}>
              <StepLabel />
            </Step>
          ))}

          <Step key="confirmation">
            <StepLabel />
          </Step>
        </Stepper>

        <EvaluationStepContent
          activeStep={activeStep}
          answerChecked={answerChecked}
          answers={answers}
          handleAnswerChange={handleAnswerChange}
          questions={questions}
          previewMode={previewMode}
          onClose={onClose}
        />
      </>
    );
  };

  const handleDialogClose = () => {
    if (loading) return;

    onClose(false);
  };

  const getDialogHeight = () => {
    if (!questions.length && transcriptionStatus === 'processing') {
      return '250px';
    }
    return '300px';
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md">
      <DialogContent className={classes.dialogContent} style={{ height: getDialogHeight() }}>
        {renderContent()}
      </DialogContent>

      {!loading && (
        <DialogActions>
          <Box className={classes.buttonsContainer} width="100%" px={2} pb={2}>
            {renderBackButtons()}
            {renderButtons()}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TechnologistEvaluationModal;
